import { useDesktop } from '@utils/hooks/useDesktop';
import { useNavigate } from 'react-router-dom';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { OrdersCounter } from './OrdersCounter';
import { NewsFeed } from './components/NewsFeed';
import {
  IconDotsVertical,
  IconMessageReportFilled,
} from '@tabler/icons-react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';
import { useDesktopMenuItems } from './hooks/useDesktopMenuItems';
import { openExternalUrl } from '@utils/navigationExternalUrl';
import { EXTERNAL_URLS } from '@utils/constants';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import logoPlick from '@assets/images/logo.svg';
import img_facebook from '@assets/images/facebook.svg';
import img_instagram from '@assets/images/instagram.svg';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { FC, useRef, useState, useEffect } from 'react';
import SupportModal from '@components/Support/SupportModal';

const Header: FC = () => {
  const { isFreemium, isTrial, remainingTrialDays, orderLimits } = useSubscription();
  const isDesktop = useDesktop();
  const navigate = useNavigate();
  const { commerce } = useGetCommerce();
  const { trackEvent } = useTracking();
  const [isNewMenuOpen, setIsNewMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [showFounderWarning, setShowFounderWarning] = useState(false);
  const [showFounderForm, setShowFounderForm] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setIsNewMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const newMenuItems = useDesktopMenuItems();

  return (
    <>
      <div className="fixed w-full z-50 bg-color-primary flex justify-between text-color-white h-16 items-center p-4 lg:px-8">
        <div className="flex items-baseline">
          <img src={logoPlick} alt="logo plick" height="25px" />
          {isDesktop && (
            <>
              <span className="ml-2 mr-4">
                Pedidos a un <strong className="bg-color-secondary rounded-sm px-1">click.</strong>
              </span>
              <a
                className="mx-2"
                href={EXTERNAL_URLS.FACEBOOK}
                onClick={(e) => {
                  e.preventDefault();
                  openExternalUrl(EXTERNAL_URLS.FACEBOOK);
                }}
              >
                <img src={img_facebook} alt="Facebook" height="25px" />
              </a>
              <a
                className="mx-2"
                href={EXTERNAL_URLS.INSTAGRAM}
                onClick={(e) => {
                  e.preventDefault();
                  openExternalUrl(EXTERNAL_URLS.INSTAGRAM);
                }}
              >
                <img src={img_instagram} alt="Instagram" height="25px" />
              </a>
            </>
          )}
        </div>
        <div className="flex gap-6">
          {/* Trial Days Badge + Button - Desktop */}
          {isDesktop && (isFreemium || isTrial) && (
            <div className="flex items-center gap-3">
              {/* Badge de días restantes para trial activo */}
              {/* {isTrial && remainingTrialDays > 0 && (
                <div className="flex items-center gap-2 bg-color-warning_yellow rounded-lg px-3 py-1.5">
                  <IconClock color="#253746" />
                  <span className="text-base font-semibold text-color-primary whitespace-nowrap">
                    {remainingTrialDays} día{remainingTrialDays !== 1 ? 's' : ''} restante
                    {remainingTrialDays !== 1 ? 's' : ''}
                  </span>
                </div>
              )} */}

              {/* Botón - cambia según estado del trial */}
              {/* <Button.Tertiary
                text={isTrial ? 'VER PLANES' : 'PROBAR GRATIS'}
                rightIcon={<IconRocket />}
                onClick={() => navigate(isTrial ? '/mi-cuenta' : '/activar-trial')}
              /> */}
            </div>
          )}
          <div className="flex items-center">
            <Tippy content="Reportar un problema">
              <button
                className="text-white mr-4 hover:text-gray-200 transition-colors"
                onClick={() => {
                  trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
                  setShowSupportModal(true);
                }}
              >
                <IconMessageReportFilled  />
              </button>
            </Tippy>
          </div>
          <div className="flex gap-6">
            <NewsFeed />
            {orderLimits?.currentMonthCount !== undefined &&
            orderLimits?.currentMonthCount !== null &&
            orderLimits?.monthlyLimit !== undefined &&
            orderLimits?.monthlyLimit !== null &&
            orderLimits?.quotaUsagePercent !== undefined &&
            orderLimits?.quotaUsagePercent !== null ? (
              <OrdersCounter
                isDesktop={isDesktop}
                current={orderLimits.currentMonthCount}
                limit={orderLimits.monthlyLimit}
                usagePercent={orderLimits.quotaUsagePercent < 100 ? orderLimits.quotaUsagePercent : 100}
              />
            ) : null}
          </div>
          {isDesktop ? (
            <div className="relative" ref={buttonRef}>
              <button
                className="outline-none bg-color-white w-[41px] h-[41px] rounded-[6px] text-color-primary flex items-center justify-center"
                onClick={() => setIsNewMenuOpen((prev) => !prev)}
              >
                <IconDotsVertical width={22} height={22} />
              </button>
              {isNewMenuOpen ? (
                <ul
                  className="absolute bg-color-white rounded-[6px] text-color-primary right-0 w-max"
                  style={{ boxShadow: '0px 1px 4px 0px #00000066' }}
                >
                  {newMenuItems.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-2 py-[8px] hover:bg-color-gray_light px-[10px] rounded-[6px] hover:cursor-pointer"
                      onClick={() => {
                        setIsNewMenuOpen(false);
                        item.onClick();
                      }}
                      style={{ color: item.color }}
                    >
                      {item.icon}
                      <span className="flex items-center">{item.label}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {showFounderWarning ? (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      ) : null}
      {showFounderForm ? <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} /> : null}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        merchantId={commerce?.id ? String(commerce.id) : ''}
        commerce={commerce}
      />
    </>
  );
};

export default Header;
